import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNotification } from '../../components/ui/NotificationToast';
import AIToolSelector from './components/AIToolSelector';
import UseCaseSelector from './components/UseCaseSelector';
import DynamicInputForm from './components/DynamicInputForm';
import PromptPreview from './components/PromptPreview';
import OptimizationPanel from './components/OptimizationPanel';
import ActionButtons from './components/ActionButtons';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import { useCaseService } from '../../services/useCaseService';
import { promptFieldService } from '../../services/promptFieldService';
import { promptTemplateService } from '../../services/promptTemplateService';
import { geminiService } from '../../services/geminiService';
import { promptGenerationService } from '../../services/promptGenerationService';

const PromptGenerator = () => {
  const { showSuccess, showError, showInfo } = useNotification();
  
  const [useCases, setUseCases] = useState([]);
  const [aiTools, setAITools] = useState([]);
  const [filteredAITools, setFilteredAITools] = useState([]);
  const [promptFields, setPromptFields] = useState([]);
  const [promptTemplate, setPromptTemplate] = useState(null);
  
  const [selectedUseCase, setSelectedUseCase] = useState('');
  const [selectedAITool, setSelectedAITool] = useState('');
  const [formData, setFormData] = useState({});
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [qualityScore, setQualityScore] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedUseCase) {
      loadAIToolsForUseCase(selectedUseCase);
    } else {
      setFilteredAITools([]);
      setSelectedAITool('');
      setPromptFields([]);
    }
  }, [selectedUseCase]);

  useEffect(() => {
    if (selectedUseCase && selectedAITool) {
      loadPromptFields(selectedUseCase, selectedAITool);
      loadPromptTemplate(selectedUseCase, selectedAITool);
    } else {
      setPromptFields([]);
      setPromptTemplate(null);
    }
  }, [selectedUseCase, selectedAITool]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [useCasesData, aiToolsData] = await Promise.all([
        useCaseService?.getAllUseCases(),
        useCaseService?.getAllAITools()
      ]);
      setUseCases(useCasesData);
      setAITools(aiToolsData);
    } catch (err) {
      showError('Error', err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAIToolsForUseCase = async (useCaseId) => {
    try {
      const tools = await useCaseService?.getAIToolsByUseCase(useCaseId);
      setFilteredAITools(tools);
      if (tools?.length > 0 && !tools?.find(t => t?.id === selectedAITool)) {
        setSelectedAITool('');
      }
    } catch (err) {
      showError('Error', err?.message || 'Failed to load AI tools');
    }
  };

  const loadPromptFields = async (useCaseId, aiToolId) => {
    try {
      const fields = await promptFieldService?.getPromptFieldsByUseCaseAndTool(useCaseId, aiToolId);
      setPromptFields(fields);
      setFormData({});
    } catch (err) {
      showError('Error', err?.message || 'Failed to load prompt fields');
    }
  };

  const loadPromptTemplate = async (useCaseId, aiToolId) => {
    try {
      const template = await promptTemplateService?.getPromptTemplate(useCaseId, aiToolId);
      setPromptTemplate(template);
    } catch (err) {
      console.error('Error loading template:', err);
      setPromptTemplate(null);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.ctrlKey || e?.metaKey) {
        switch (e?.key?.toLowerCase()) {
          case 'enter':
            e?.preventDefault();
            generatePrompt();
            break;
          case 'c':
            if (generatedPrompt) {
              e?.preventDefault();
              handleCopyPrompt();
            }
            break;
          case 'o':
            e?.preventDefault();
            if (generatedPrompt) handleOptimize();
            break;
          case 'z':
            e?.preventDefault();
            handleUndo();
            break;
          case 'y':
            e?.preventDefault();
            handleRedo();
            break;
          case 'k':
            e?.preventDefault();
            handleClear();
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [generatedPrompt, historyIndex, history]);

  const generatePrompt = async () => {
    if (!selectedUseCase || !selectedAITool) {
      showError('Missing Information', 'Please select both Use Case and AI Tool');
      return;
    }

    if (promptFields?.length === 0) {
      showError('No Fields', 'No prompt fields configured for this combination');
      return;
    }

    const requiredFields = promptFields?.filter(f => f?.isRequired);
    const missingFields = requiredFields?.filter(f => !formData?.[f?.fieldName]);
    
    if (missingFields?.length > 0) {
      showError('Missing Required Fields', `Please fill in: ${missingFields?.map(f => f?.fieldLabel)?.join(', ')}`);
      return;
    }

    let filledPrompt = '';
    
    // Use template if available, otherwise use default format
    if (promptTemplate?.templateText) {
      filledPrompt = promptTemplate?.templateText;
      
      // Replace placeholders with user input
      promptFields?.forEach(field => {
        const value = formData?.[field?.fieldName];
        if (value) {
          // Replace [Field Label] with actual value
          const placeholder = `[${field?.fieldLabel}]`;
          filledPrompt = filledPrompt?.replace(new RegExp(placeholder?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
        }
      });
    } else {
      // Default format without template
      promptFields?.forEach(field => {
        const value = formData?.[field?.fieldName];
        if (value) {
          filledPrompt += `${field?.fieldLabel}: ${value}\n`;
        }
      });
    }

    // Enhance the filled prompt with Gemini AI
    try {
      setIsEnhancing(true);
      showInfo('Enhancing...', 'AI is optimizing your prompt');
      
      const enhancedPrompt = await geminiService?.enhancePrompt(filledPrompt);
      
      setGeneratedPrompt(enhancedPrompt);
      setHistory([...history?.slice(0, historyIndex + 1), enhancedPrompt]);
      setHistoryIndex(historyIndex + 1);

      // Track the generation in database
      try {
        await promptGenerationService?.trackGeneration({
          useCaseId: selectedUseCase,
          aiToolId: selectedAITool,
          promptFields: formData,
          generatedPrompt: enhancedPrompt,
          userAgent: navigator?.userAgent
        });
      } catch (trackError) {
        console.error('Failed to track generation:', trackError);
        // Don't show error to user, tracking is non-critical
      }

      showSuccess('Success', 'Prompt enhanced successfully');
    } catch (err) {
      showError('Enhancement Failed', err?.message || 'Failed to enhance prompt. Please try again.');
      // Fall back to the filled prompt without enhancement
      setGeneratedPrompt(filledPrompt);
      setHistory([...history?.slice(0, historyIndex + 1), filledPrompt]);
      setHistoryIndex(historyIndex + 1);
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator?.clipboard?.writeText(generatedPrompt);
      showSuccess('Copied!', 'Prompt copied to clipboard');
    } catch (err) {
      showError('Error', 'Failed to copy prompt');
    }
  };

  const handleOptimize = () => {
    setIsOptimizing(true);
    setTimeout(() => {
      setQualityScore(Math?.floor(Math?.random() * 20) + 80);
      setSuggestions([
        'Consider adding more specific details',
        'Try using more descriptive language',
        'Include context for better results'
      ]);
      setIsOptimizing(false);
      showInfo('Analysis Complete', 'Prompt quality analyzed');
    }, 1500);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setGeneratedPrompt(history?.[historyIndex - 1]);
      showInfo('Undo', 'Reverted to previous version');
    }
  };

  const handleRedo = () => {
    if (historyIndex < history?.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setGeneratedPrompt(history?.[historyIndex + 1]);
      showInfo('Redo', 'Restored next version');
    }
  };

  const handleClear = () => {
    setFormData({});
    setGeneratedPrompt('');
    setQualityScore(null);
    setSuggestions([]);
    setHistory([]);
    setHistoryIndex(-1);
    showInfo('Cleared', 'Form and prompt cleared');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading prompt generator...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Get Prompt</title>
        <meta name="description" content="Generate optimized prompts for any AI tool" />
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-foreground mb-4">Configure Your Prompt</h2>
              
              <div className="space-y-4">
                <UseCaseSelector
                  value={selectedUseCase}
                  onChange={setSelectedUseCase}
                  useCases={useCases}
                />

                <AIToolSelector
                  value={selectedAITool}
                  onChange={setSelectedAITool}
                  aiTools={filteredAITools}
                  disabled={!selectedUseCase}
                />

                <DynamicInputForm
                  promptFields={promptFields}
                  formData={formData}
                  onChange={setFormData}
                />
              </div>
            </div>

            <ActionButtons
              onGenerate={generatePrompt}
              onCopy={handleCopyPrompt}
              onClear={handleClear}
              disabled={!selectedUseCase || !selectedAITool || isEnhancing}
            />
          </div>

          <div className="space-y-6">
            <PromptPreview
              prompt={generatedPrompt}
              onCopy={handleCopyPrompt}
              characterCount={generatedPrompt?.length || 0}
              onOptimize={handleOptimize}
              isOptimizing={isOptimizing}
              isEnhancing={isEnhancing}
            />

            {(qualityScore || suggestions?.length > 0) && (
              <OptimizationPanel
                qualityScore={qualityScore}
                suggestions={suggestions}
              />
            )}

            <KeyboardShortcuts />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptGenerator;